import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { onAssistantQuestion, takePendingQuestion } from "@/lib/assistantPendingQuestion";
// El MISMO logo que la barra lateral, no el icono suelto de /ulture-ai.png: así
// el asistente se presenta con la marca de la app y no con un dibujo aparte.
import ultureLogo from "@/assets/ulture-logo-transparent.png";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Send, Loader2, Mic, Square, Trash2, User, MessageSquare, Camera, Bug, FileText, FileSpreadsheet, Image as ImageIcon, ExternalLink, Plus, BookOpenCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useAssistantHistory } from "@/hooks/useAssistantHistory";
import { useAssistantStream, assistantErrorKey, autoValido, contenidoParaHistorial, restoredDocs, type AssistantAuto, type ToolActivity, type AssistantCardGroup, type AssistantDocSpec, type AssistantNavTarget } from "@/hooks/useAssistantStream";
import { useLocation } from "react-router-dom";
import { AssistantTaskCards, type AssistantTaskCardItem } from "@/components/assistant/AssistantTaskCards";
import { AssistantReportCard } from "@/components/assistant/AssistantReportCard";
import { ToolActivityChips } from "@/components/assistant/ToolActivityChips";
import { AssistantMarkdown } from "@/components/assistant/AssistantMarkdown";
import { AssistantActionButtons, collectActionButtons } from "@/components/assistant/AssistantActionButtons";
import { suggestionMode, suggestionRoute, suggestionsForRoute, suggestionText } from "@/components/assistant/assistantSuggestions";
import { AssistantModeChip, AssistantModeMenu } from "@/components/assistant/AssistantModeMenu";
import { ASSISTANT_MODES, atTokenAt, filterModes, modeById, stripAtToken, type AssistantModeId } from "@/components/assistant/assistantModes";
import { AssistantCuadernoCard } from "@/components/assistant/AssistantCuadernoCard";
import { leerSeleccion } from "@/lib/cuaderno/seleccion";
import { AssistantCuadernoChangeCard } from "@/components/assistant/AssistantCuadernoChangeCard";
import { AssistantEmailCard } from "@/components/assistant/AssistantEmailCard";
import { AssistantDocumentChip } from "@/components/assistant/AssistantDocumentChip";
import { AssistantDocumentFillCard } from "@/components/assistant/AssistantDocumentFillCard";
import { AssistantMarcaCambioCard } from "@/components/assistant/AssistantMarcaCambioCard";
import { AssistantEstructuraCambioCard } from "@/components/assistant/AssistantEstructuraCambioCard";
import { ACEPTA, DocumentoNoLeible, tipoDeFichero } from "@/lib/documentos/leer";
import { subirDocumento, type DocumentoAdjunto } from "@/lib/documentos/subir";
import { AssistantCuadernoTitular } from "@/components/assistant/AssistantCuadernoTitular";
import { useCuadernoCampoEnabled } from "@/hooks/useCuadernoCampoEnabled";
import { cn } from "@/lib/utils";
import { estadoDiagnostico, limpiarNombrePlaga } from "@/lib/pestDiagnosis";
import { leerDesenlace } from "@/lib/pests/desenlaceIA";
import { AiNoPestMessage, AiOutcomeChips } from "@/components/pests/AiOutcomeChips";
import { formatDistanceToNow } from "date-fns";
import { es, enUS, it as itLocale, arSA } from "date-fns/locale";

export interface RecognitionPayload {
  identified: boolean;
  data?: {
    pestName: string;
    scientificName: string;
    category: string;
    severity: string;
    affectedCrop: string;
    symptoms: string[];
    recommendedTreatment: string;
    confidence: number;
    description: string;
    /* IA con la lista del cultivo (F7, 05-10-2026): solo llegan cuando se analizó con ella. */
    /** pest · beneficial (auxiliar) · abiotic · healthy · unclear. */
    outcome?: string | null;
    /** La plaga que nombró NO está en la lista del cultivo. */
    outsideList?: boolean | null;
    /** De qué parte de la lista salió (crop_list, history, watch, company, catalog). */
    listSource?: string | null;
  };
  /** Qué más podría ser, para cuando el diagnóstico es solo una sospecha. */
  candidates?: string[];
  /** Sin plaga (sana o «no se puede saber»), el `outcome` viene fuera de `data` (F7). */
  outcome?: string | null;
  /** El cultivo con que se analizó (código SIEX), para «Fuera de la lista de tomate». */
  cropCode?: number | null;
}

/** Fuente citada por el asistente (registro MAPA o búsqueda web restringida). */
interface AssistantSource {
  title: string;
  url: string;
  consulted_at: string;
  kind: "mapa" | "web";
}

interface Message {
  /** Estable para poder actualizar la burbuja en streaming por id, no por índice. */
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Solo asistente: pending (esperando) → streaming (llegan deltas) → done | error. */
  status?: 'pending' | 'streaming' | 'done' | 'error';
  /** Herramientas consultadas durante la respuesta («Consultando inventario…»). */
  toolEvents?: ToolActivity[];
  /** La conexión se cortó a mitad: la respuesta es parcial. */
  interrupted?: boolean;
  timestamp?: Date;
  recognition?: RecognitionPayload;
  onCreateTask?: () => void;
  imagePreview?: string;
  sources?: AssistantSource[];
  /** Botones «llévame ahí»: páginas de la app, con su nombre ya en el idioma del usuario. */
  nav?: AssistantNavTarget[];
  /** Filas que se pintan como tarjetas debajo del texto (no las escribe el modelo). */
  cards?: AssistantCardGroup[];
  /** Informes (PDF y/o Excel) preparados durante la respuesta; se guardan con ella. */
  docs?: AssistantDocSpec[];
  /** Solo persona: documentos que subió con este mensaje (Excel, CSV, Word; 28-09-2026). */
  documentos?: DocumentoAdjunto[];
  /** Se pidió «@ Cuaderno de campo» y la edge contestó en modo normal (apagado o edge anterior). */
  modeNotServed?: boolean;
  /**
   * Turno AUTOMÁTICO de la app (CU7: seguir tras «Aplicar» en el cuaderno): viaja en
   * el historial para que el modelo sepa por qué habla, pero NO se pinta como si lo
   * hubiera escrito la persona.
   */
  hidden?: boolean;
  /**
   * CU9 (28-09-2026): las tarjetas llegaron en ESTE turno (no del historial). Un cambio
   * del DOCUMENTO del cuaderno se aplica solo al llegar; uno del historial no.
   */
  liveCards?: boolean;
}

/** Lo que manda el chat en el seguimiento automático (el servidor pone su propio texto: mode.ts). */
const TEXTO_SEGUIMIENTO_CUADERNO = "[aplicado] sigue";
/** El de «Guardar en la cabecera» (F4 de «Personalizar informes»): rehace el informe (mode.ts#MENSAJE_AUTO_MARCA). */
const TEXTO_SEGUIMIENTO_MARCA = "[guardado] rehaz el informe";

/** Los documentos guardados con un mensaje de la persona (`context.documentos`), al reabrir la conversación. */
function documentosGuardados(raw: unknown): DocumentoAdjunto[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((d): d is Record<string, unknown> => !!d && typeof d === "object" && typeof (d as { id?: unknown }).id === "string" && typeof (d as { nombre?: unknown }).nombre === "string")
    .map((d) => ({
      id: String(d.id),
      nombre: String(d.nombre),
      tipo: (["xlsx", "xls", "csv", "docx"].includes(String(d.tipo)) ? d.tipo : "xlsx") as DocumentoAdjunto["tipo"],
      hojas: Number(d.hojas) || 0,
      filas: Number(d.filas) || 0,
    }));
}

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

interface IpmAiAssistantProps {
  /** Called when the user sends a photo. Parent runs analysis + pushes the
   *  assistant recognition message via the ref. Returning a promise lets the
   *  composer show a loading state. */
  onAnalyzePhoto?: (file: File) => Promise<void> | void;
  onCreateTaskFromRecognition?: (recognition: RecognitionPayload) => void;
  /** Dentro de la ventana del micrófono: composer en flujo (no fijo al viewport)
   *  y sin cromo propio — la hoja pone el marco. */
  embedded?: boolean;
  /**
   * Si hay conversación a la vista o no. La hoja del micrófono lo usa para
   * estirarse a pantalla completa en cuanto se manda el primer mensaje, y para
   * volver a su tamaño con «Nueva conversación».
   *
   * Se avisa del ESTADO, no del envío: así también sale estirada cuando se
   * recupera una conversación anterior del historial, que es cuando más hay que
   * leer. Y al recuperarla no hay estirón porque nace ya con el alto puesto.
   */
  onConversationChange?: (hasMessages: boolean) => void;
}

type AudioState = "idle" | "recording" | "transcribing";


export interface IpmAiAssistantHandle {
  pushRecognitionMessage: (recognition: RecognitionPayload, onCreateTask?: () => void) => void;
}

const IpmAiAssistant = forwardRef<IpmAiAssistantHandle, IpmAiAssistantProps>(function IpmAiAssistant(
  { onAnalyzePhoto, onCreateTaskFromRecognition, embedded = false, onConversationChange }: IpmAiAssistantProps,
  ref,
) {
  const [messages, setMessages] = useState<Message[]>([]);
  /* Copia siempre-fresca para construir el historial a enviar: el closure de
     sendMessage veía `messages` desfasado y el historial llegaba con un turno
     de retraso. */
  const messagesRef = useRef<Message[]>([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const hasMessages = messages.length > 0;
  useEffect(() => { onConversationChange?.(hasMessages); }, [hasMessages, onConversationChange]);
  const [input, setInput] = useState("");
  /* «@ Cuaderno de campo» (CQ4b): el modo puesto (chip con ×), el menú que abre
     la «@» (dónde empieza y lo tecleado detrás) y la opción resaltada. */
  const [mode, setMode] = useState<AssistantModeId | null>(null);
  const [modeMenu, setModeMenu] = useState<{ start: number; query: string } | null>(null);
  const [modeActive, setModeActive] = useState<AssistantModeId | null>(null);
  /* M5 (D14): el titular del cuaderno. null = el de la empresa; una finca = la de OTRO
     titular (se elige junto al chip si la empresa tiene más de uno). */
  const [cuadernoTitular, setCuadernoTitular] = useState<string | null>(null);
  const cuadernoTitularRef = useRef<string | null>(null);
  cuadernoTitularRef.current = cuadernoTitular;
  /* DV3: una pregunta dictada en el panel de tareas por voz llega aquí ya escrita
     (la manda la persona: puede corregirla antes). Al montar se recoge la pendiente;
     con el chat ya abierto, llega por el evento. */
  useEffect(() => {
    const q = takePendingQuestion();
    if (q) setInput(q);
    return onAssistantQuestion((text) => setInput(text));
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [audioState, setAudioState] = useState<AudioState>("idle");
  const [attachedPhoto, setAttachedPhoto] = useState<File | null>(null);
  const [attachedPhotoPreview, setAttachedPhotoPreview] = useState<string | null>(null);
  const [isAnalyzingPhoto, setIsAnalyzingPhoto] = useState(false);
  /* Documentos (28-09-2026): los ya subidos que van con el PRÓXIMO mensaje, y los
     que están subiendo ahora (chip con «Subiendo…»). */
  const [adjuntos, setAdjuntos] = useState<DocumentoAdjunto[]>([]);
  const [subiendoDocs, setSubiendoDocs] = useState<Array<{ clave: string; nombre: string; tipo: DocumentoAdjunto["tipo"] }>>([]);
  const adjuntosRef = useRef<DocumentoAdjunto[]>([]);
  adjuntosRef.current = adjuntos;
  const docInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { t, i18n } = useTranslation();
  const { company, profile, role } = useAuth();
  /* Subir documentos: admin y encargado (los que tienen informes; RLS igual). */
  const puedeSubirDocumentos = !!company?.id && (role === "admin" || role === "manager");
  /* Los modos que esta empresa tiene: el cuaderno solo con su interruptor
     (platform_settings.cuaderno_campo, D24). Sin ninguno, ni «@» ni entrada en
     el «+»: el chat de siempre. */
  const cuadernoCampo = useCuadernoCampoEnabled();
  const availableModes = ASSISTANT_MODES.filter((m) => m.id !== "cuaderno" || cuadernoCampo.allowed);
  const modeDef = mode && availableModes.some((m) => m.id === mode) ? modeById(mode) : undefined;
  const menuModes = modeMenu ? filterModes(availableModes, modeMenu.query, (m) => t(m.labelKey)) : [];
  /* El modo que viaja con el próximo mensaje, siempre fresco (handleSend va memorizado). */
  const modeRef = useRef<AssistantModeId | null>(null);
  modeRef.current = modeDef?.id ?? null;

  const history = useAssistantHistory();
  const stream = useAssistantStream();
  /* La pantalla desde la que se abre el chat decide las sugerencias del chat
     vacío. En el teléfono el chat es una hoja encima de esa pantalla; en el
     ordenador es su propia página (/advanced), y ahí cuenta la última pantalla
     de la app en la que estuviste (`suggestionRoute`). */
  const { pathname } = useLocation();
  const suggestions = suggestionsForRoute(suggestionRoute(pathname));

  // Saludo por franja horaria + nombre de pila, como el héroe del Panel.
  const firstName = profile?.full_name?.trim().split(/\s+/)[0] || "";
  const hour = new Date().getHours();
  const greeting =
    hour < 12
      ? t("dashboard.hero.goodMorning", "Buenos días")
      : hour < 21
      ? t("dashboard.hero.goodAfternoon", "Buenas tardes")
      : t("dashboard.hero.goodEvening", "Buenas noches");

  useImperativeHandle(ref, () => ({
    pushRecognitionMessage: (recognition, onCreateTask) => {
      const msg: Message = {
        id: newId(),
        role: 'assistant',
        content: '',
        status: 'done',
        timestamp: new Date(),
        recognition,
        onCreateTask,
      };
      setMessages((prev) => [...prev, msg]);
    },
  }), []);



  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-grow textarea like WhatsApp
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxPx = Math.round(window.innerHeight * 0.4);
    el.style.height = Math.min(el.scrollHeight, maxPx) + 'px';
  }, [input]);

  useEffect(() => {
    if (history.loadedMessages.length > 0) {
      setMessages(
        history.loadedMessages.map((m) => {
          // El contexto guardado por el backend trae fuentes, traza de
          // herramientas e informes: al recargar, la conversación vuelve completa.
          const c = m.context as {
            sources?: AssistantSource[];
            nav?: AssistantNavTarget[];
            tools?: Array<{ name: string; ok?: boolean }>;
            cards?: AssistantCardGroup[];
            docs?: unknown;
          } | null | undefined;
          const docs = restoredDocs(c?.docs);
          const documentos = m.role === "user" ? documentosGuardados((m.context as { documentos?: unknown } | null | undefined)?.documentos) : [];
          return {
            id: newId(),
            role: m.role as 'user' | 'assistant',
            content: m.content,
            // El turno automático tras «Aplicar» (CU7) no lo escribió la persona: no se pinta.
            ...(m.role === "user" && (m.context as { auto?: unknown } | null | undefined)?.auto ? { hidden: true } : {}),
            status: 'done' as const,
            timestamp: new Date(m.created_at),
            ...(Array.isArray(c?.sources) && c.sources.length ? { sources: c.sources } : {}),
            ...(Array.isArray(c?.nav) && c.nav.length ? { nav: c.nav } : {}),
            ...(Array.isArray(c?.cards) && c.cards.length ? { cards: c.cards } : {}),
            ...(docs.length ? { docs } : {}),
            ...(documentos.length ? { documentos } : {}),
            ...(Array.isArray(c?.tools) && c.tools.length
              ? {
                  toolEvents: c.tools.map((tl, i) => ({
                    id: `restored-${i}`,
                    name: tl.name,
                    status: (tl.ok === false ? "error" : "done") as ToolActivity["status"],
                  })),
                }
              : {}),
          };
        })
      );
      /* Una conversación que iba en modo cuaderno vuelve con su chip (el turno
         del asistente guarda `context.mode`); una normal, sin él. */
      const conModo = [...history.loadedMessages].reverse().find(
        (m) => m.role === "assistant" && (m.context as { mode?: unknown } | null | undefined)?.mode === "cuaderno",
      );
      setMode(conModo ? "cuaderno" : null);
      // …y con su titular (M5), si era el de otra finca.
      const tit = (conModo?.context as { cuaderno?: { titularLocationId?: unknown } } | null | undefined)?.cuaderno?.titularLocationId;
      setCuadernoTitular(typeof tit === "string" ? tit : null);
    }
  }, [history.loadedMessages]);

  /** Actualiza la burbuja del asistente por id (nunca por índice). */
  const patchMessage = (id: string, patch: Partial<Message> | ((m: Message) => Partial<Message>)) => {
    setMessages(prev => prev.map(m =>
      m.id === id ? { ...m, ...(typeof patch === "function" ? patch(m) : patch) } : m
    ));
  };

  /** Sube los documentos elegidos (hasta 3 a la vez) y los deja listos para el próximo mensaje. */
  const subirDocs = async (files: FileList | null) => {
    if (!files || !company?.id) return;
    for (const file of Array.from(files).slice(0, 3)) {
      const tipo = tipoDeFichero(file.name);
      if (!tipo) {
        toast.error(t("ipmAssistant.documents.errorTipo", "Ese tipo de fichero todavía no se puede leer: sube un Excel, un CSV o un Word."));
        continue;
      }
      const clave = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setSubiendoDocs((p) => [...p, { clave, nombre: file.name, tipo }]);
      try {
        const d = await subirDocumento(supabase, company.id, file);
        setAdjuntos((p) => [...p, d]);
      } catch (e) {
        console.error("[IpmAiAssistant] documento:", e);
        const motivo = e instanceof DocumentoNoLeible ? e.motivo : "subida";
        const claveTexto = { tipo: "errorTipo", tamano: "errorTamano", vacio: "errorVacio", roto: "errorRoto", subida: "errorSubida" }[motivo];
        toast.error(t(`ipmAssistant.documents.${claveTexto}`));
      } finally {
        setSubiendoDocs((p) => p.filter((x) => x.clave !== clave));
      }
    }
  };

  const sendMessage = async (messageText?: string, modeOverride?: AssistantModeId | null, opts?: { auto?: AssistantAuto }) => {
    const sendMode = modeOverride !== undefined ? modeOverride : modeRef.current;
    // Cada seguimiento en su modo: el del cuaderno en el cuaderno, el de la cabecera en el normal (F4).
    const auto = opts?.auto && autoValido(sendMode, opts.auto) ? opts.auto : undefined;
    // Los documentos subidos van con ESTE mensaje (el seguimiento automático no los lleva).
    const docsNuevos = auto ? [] : adjuntosRef.current;
    const text =
      messageText ||
      input.trim() ||
      (docsNuevos.length ? t("ipmAssistant.documents.defaultMessage", "Te paso este documento: {{name}}", { name: docsNuevos.map((d) => d.nombre).join(", ") }) : "");
    if (!text || isLoading) return;
    // El modo que la edge dice que atendió (trama `meta`); sin él, contestó en normal.
    let servedMode: string | undefined;
    /* Todos los documentos de la conversación (los últimos 5) viajan en cada turno:
       «rellénalo» o «ordénalo por día» pueden llegar tres mensajes después. */
    const docsConversacion = [...messagesRef.current.flatMap((m) => m.documentos ?? []), ...docsNuevos];
    const docIds = [...new Set(docsConversacion.map((d) => d.id))].slice(-5);

    const userMessage: Message = {
      id: newId(),
      role: 'user',
      content: text,
      timestamp: new Date(),
      ...(auto ? { hidden: true } : {}),
      ...(docsNuevos.length ? { documentos: docsNuevos } : {}),
    };
    const placeholderId = newId();
    const placeholder: Message = {
      id: placeholderId,
      role: 'assistant',
      content: '',
      status: 'pending',
      toolEvents: [],
      timestamp: new Date(),
    };

    /* Historial a enviar: lo que había ANTES de este mensaje (el mensaje nuevo
       viaja aparte como `message`), sin tarjetas de reconocimiento ni vacíos.
       Se construye desde messagesRef — el closure desfasado mandaba el
       historial con un turno de retraso. */
    const historyToSend = messagesRef.current
      .filter(m => m.content && !m.recognition)
      .slice(-12)
      .map(m => ({ role: m.role, content: m.role === 'assistant' ? contenidoParaHistorial(m.content, m.docs) : m.content }));

    setMessages(prev => [...prev, userMessage, placeholder]);
    // El seguimiento automático no toca lo que la persona esté escribiendo.
    if (!auto) setInput("");
    if (docsNuevos.length) setAdjuntos([]);
    setIsLoading(true);

    let convId = history.activeConversationId;
    if (!convId) {
      convId = await history.createConversation(text);
    }

    /* Acumulador local del texto recibido. NO se puede leer de messagesRef al
       terminar: ese ref lo sincroniza un efecto que aún no ha corrido cuando la
       promesa resuelve, así que la última tanda (con el backend json, TODA la
       respuesta) todavía no está — se daba por vacía y se borraba la burbuja. */
    let streamed = "";
    // Informes del turno, por si los guarda el cliente (rama json o Stop, abajo).
    const streamedDocs: AssistantDocSpec[] = [];

    try {
      const result = await stream.send(
        {
          message: text,
          language: i18n.language,
          conversationId: convId,
          history: historyToSend,
          ...(auto ? { auto } : {}),
          ...(docIds.length ? { documents: docIds, attached: docsNuevos.map((d) => d.id) } : {}),
          ...(sendMode === "cuaderno"
            ? {
                mode: "cuaderno" as const,
                cuaderno: {
                  year: new Date().getFullYear(),
                  // M5: el titular elegido; sin él (o con uno solo), el de la empresa.
                  ...(cuadernoTitularRef.current ? { titular_location_id: cuadernoTitularRef.current } : {}),
                },
              }
            : {}),
        },
        {
          onDelta: (delta) => {
            streamed += delta;
            patchMessage(placeholderId, (m) => ({
              content: m.content + delta,
              status: 'streaming',
            }));
          },
          /* Texto en directo (protocolo v2): el servidor SUSTITUYE lo que hay
             en pantalla — a mitad de turno para retirar un texto previo a una
             herramienta y al final con la versión saneada definitiva, que es la
             que se guarda. El acumulador local sigue a la pantalla. */
          onReplace: (text) => {
            streamed = text;
            patchMessage(placeholderId, { content: text, status: 'streaming' });
          },
          onTool: (tool) => {
            patchMessage(placeholderId, (m) => {
              const tools = [...(m.toolEvents ?? [])];
              const idx = tools.findIndex(x => x.id === tool.id);
              if (idx >= 0) tools[idx] = { ...tools[idx], ...tool, label: tools[idx].label ?? tool.label };
              else tools.push(tool);
              return { toolEvents: tools, status: m.status === 'pending' ? 'streaming' : m.status };
            });
          },
          onMeta: (meta) => {
            if (meta.mode) servedMode = meta.mode;
            if (meta.sources?.length) {
              patchMessage(placeholderId, { sources: meta.sources as AssistantSource[] });
            }
            if (meta.nav?.length) {
              patchMessage(placeholderId, { nav: meta.nav });
            }
            // Llega un grupo por trama: se acumulan en vez de pisarse.
            if (meta.cards?.length) {
              patchMessage(placeholderId, (m) => ({ cards: [...(m.cards ?? []), ...meta.cards!], liveCards: true }));
            }
            if (meta.docs?.length) {
              streamedDocs.push(...meta.docs);
              patchMessage(placeholderId, (m) => ({ docs: [...(m.docs ?? []), ...meta.docs!] }));
            }
          },
        },
      );

      const finalContent = streamed.trim();

      if (!finalContent) {
        // Nada llegó (abort temprano o respuesta vacía): fuera el globo.
        setMessages(prev => prev.filter(m => m.id !== placeholderId));
        if (!result.aborted) toast.error(t("ipmAssistant.noResponse"));
        return;
      }

      patchMessage(placeholderId, {
        status: 'done',
        // Parcial por corte o stop: se marca para el pie «respuesta parcial».
        interrupted: result.aborted || undefined,
        // Se pidió el cuaderno y la edge contestó en normal: se dice, no se disimula.
        modeNotServed: (sendMode === "cuaderno" && result.transport === "sse" && servedMode !== "cuaderno") || undefined,
      });

      /* Quién persiste, sin solapes ni huecos:
         - json (backend antiguo): nadie más lo hace → aquí.
         - sse + Stop: el servidor ve el corte (writer.clientGone) y NO escribe
           → aquí, que es donde está el parcial.
         - sse completo: persiste el servidor; guardar aquí duplicaría filas.
           Solo se refresca la lista de conversaciones (fetchConversations vive
           dentro de saveMessage, que en esta rama no se llama). */
      if (convId && (result.transport === "json" || result.aborted)) {
        history.saveMessage(convId, 'user', text, auto ? { v: 1, auto } : docsNuevos.length ? { v: 1, documentos: docsNuevos } : undefined);
        // Con sus informes: al reabrir la conversación tienen que seguir ahí.
        history.saveMessage(convId, 'assistant', finalContent, streamedDocs.length ? { v: 1, docs: streamedDocs } : undefined);
      } else if (convId) {
        history.fetchConversations();
      }
    } catch (error: unknown) {
      console.error('AI Assistant error:', error);
      if (streamed) {
        // Se cortó a mitad: conservar lo recibido, marcado como parcial.
        patchMessage(placeholderId, { status: 'done', interrupted: true });
      } else {
        setMessages(prev => prev.filter(m => m.id !== placeholderId));
      }
      toast.error(t(assistantErrorKey(error), t("ipmAssistant.errorComm")));
    } finally {
      setIsLoading(false);
    }
  };

  /* CU7 (27-09): tras un «Aplicar» que sale bien en una tarjeta del cuaderno, el
     asistente sigue SOLO con la siguiente pregunta. Un seguimiento por aplicación
     (dos «Aplicar» seguidos se juntan en uno) y, si el chat está contestando, se
     espera a que acabe. Si «Aplicar» falla, la tarjeta no avisa y no se manda nada. */
  /* F4 de «Personalizar informes» (29-09): tras «Guardar en la cabecera», lo mismo en
     el modo normal: el asistente rehace el informe con el dato ya guardado. */
  const seguimientoPendiente = useRef<AssistantAuto | null>(null);
  const [seguimientoTick, setSeguimientoTick] = useState(0);
  const seguirTrasAplicar = useCallback(() => {
    seguimientoPendiente.current = "cuaderno_aplicado";
    setSeguimientoTick((n) => n + 1);
  }, []);
  const seguirTrasGuardarCabecera = useCallback(() => {
    seguimientoPendiente.current = "marca_guardada";
    setSeguimientoTick((n) => n + 1);
  }, []);
  useEffect(() => {
    const pendiente = seguimientoPendiente.current;
    if (!pendiente || isLoading) return;
    seguimientoPendiente.current = null;
    if (pendiente === "marca_guardada") void sendMessage(TEXTO_SEGUIMIENTO_MARCA, null, { auto: "marca_guardada" });
    else void sendMessage(TEXTO_SEGUIMIENTO_CUADERNO, "cuaderno", { auto: "cuaderno_aplicado" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seguimientoTick, isLoading]);

  /** Elige un modo: quita la «@» que abrió el menú, pone el chip y deja el foco en la caja. */
  const selectMode = (id: AssistantModeId) => {
    setMode(id);
    if (modeMenu) {
      const tok = modeMenu;
      setInput((prev) => stripAtToken(prev, tok));
    }
    setModeMenu(null);
    setModeActive(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setInput(v);
    const tok = availableModes.length ? atTokenAt(v, e.target.selectionStart ?? v.length) : null;
    setModeMenu(tok);
    if (tok) setModeActive((prev) => prev ?? availableModes[0]?.id ?? null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    /* Con el menú de la «@» abierto, las flechas eligen, Enter o Tab ponen el
       modo y Escape lo cierra (el foco sigue en la caja). */
    if (modeMenu && menuModes.length) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const i = Math.max(0, menuModes.findIndex((m) => m.id === modeActive));
        const next = (i + (e.key === "ArrowDown" ? 1 : menuModes.length - 1)) % menuModes.length;
        setModeActive(menuModes[next].id);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        selectMode(menuModes.find((m) => m.id === modeActive)?.id ?? menuModes[0].id);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setModeMenu(null);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearAttachedPhoto = useCallback(() => {
    setAttachedPhoto(null);
    setAttachedPhotoPreview((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handlePickPhoto = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAttachedPhotoPreview((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return url;
    });
    setAttachedPhoto(file);
  }, []);

  // Revoke object URL on unmount
  useEffect(() => () => {
    setAttachedPhotoPreview((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleSend = useCallback(async () => {
    if (isLoading || isAnalyzingPhoto) return;
    // Photo branch
    if (attachedPhoto && onAnalyzePhoto) {
      const file = attachedPhoto;
      const previewUrl = attachedPhotoPreview ?? URL.createObjectURL(file);
      const text = input.trim();
      const userMsg: Message = {
        id: newId(),
        role: 'user',
        content: text,
        timestamp: new Date(),
        imagePreview: previewUrl,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      // Clear UI attachment but DO NOT revoke the URL — the message bubble owns it now.
      setAttachedPhoto(null);
      setAttachedPhotoPreview(null);
      setIsAnalyzingPhoto(true);
      try {
        await onAnalyzePhoto(file);
      } catch (err) {
        console.error('Photo analysis error:', err);
      } finally {
        setIsAnalyzingPhoto(false);
      }
      return;
    }
    // Text branch
    sendMessage();
  }, [attachedPhoto, attachedPhotoPreview, input, isLoading, isAnalyzingPhoto, onAnalyzePhoto]);



  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // iOS Safari does not support audio/webm — pick a supported container.
      const candidates = ["audio/webm", "audio/mp4", "audio/ogg"];
      const mimeType =
        typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported
          ? candidates.find((m) => MediaRecorder.isTypeSupported(m))
          : undefined;
      const ext = mimeType?.includes("mp4") ? "m4a" : mimeType?.includes("ogg") ? "ogg" : "webm";

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType || mimeType || "audio/webm" });
        chunksRef.current = [];

        if (audioBlob.size < 1000) {
          setAudioState("idle");
          return;
        }

        setAudioState("transcribing");

        try {
          const formData = new FormData();
          formData.append("audio", audioBlob, `recording.${ext}`);
          formData.append("language", i18n.language);
          if (company?.id) formData.append("company_id", company.id);
          /* CQ5: con el chip «Cuaderno de campo» puesto, la transcripción usa
             además el vocabulario del cuaderno (NIF, ROPO, SIGPAC, las máquinas
             de la empresa…). SOLO entonces: el micro normal, la tarea por voz y
             los partes siguen sin contexto (A11). Lo dictado va a la caja y NO
             se envía solo: se revisa y se pulsa Enviar (DV3, D21). */
          if (modeRef.current === "cuaderno") formData.append("context", "cuaderno");

          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token;

          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-transcribe`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${accessToken}` },
              body: formData,
            }
          );

          if (!response.ok) {
            const detail = await response.text().catch(() => "");
            throw new Error(`Transcription failed (${response.status}) ${detail}`.trim());
          }

          const result = await response.json();
          if (result.text?.trim()) {
            setInput(result.text.trim());
          }
        } catch (err: any) {
          console.error("Transcription error:", err);
          toast.error(err.message || "Error transcribing audio");
        } finally {
          setAudioState("idle");
        }
      };

      mediaRecorder.start();
      setAudioState("recording");
    } catch (err) {
      console.error("Mic permission error:", err);
      toast.error(t("ipmAssistant.micPermissionError"));
      setAudioState("idle");
    }
  }, [i18n.language, t, company?.id]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleMicClick = useCallback(() => {
    if (audioState === "recording") {
      stopRecording();
    } else if (audioState === "idle") {
      startRecording();
    }
  }, [audioState, startRecording, stopRecording]);

  const dateFnsLocale =
    i18n.language === "es" ? es
    : i18n.language === "it" ? itLocale
    : i18n.language === "ar" ? arSA
    : enUS;

  return (
    <div className={cn(
      "flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-card",
      embedded
        ? "rounded-none border-0 shadow-none"
        : "rounded-none border-0 shadow-lg lg:rounded-2xl lg:border lg:border-border/40"
    )}>


      {/* ── Loading history indicator ── */}
      {history.isLoadingHistory && (
        <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t("ipmAssistant.loadingConversation", "Cargando conversación...")}
        </div>
      )}

      {/* ── Chat Area ── */}
      <div
        ref={scrollRef}
        className={cn(
          "flex-1 min-h-0 overflow-y-auto overscroll-contain",
          embedded ? "pb-0" : "pb-[calc(var(--safe-bottom)+96px)] lg:pb-0"
        )}
        style={{ WebkitOverflowScrolling: 'touch' }}
      >



        <div className="p-4 space-y-4">
          {/* Con conversación en marcha: pastilla para empezar de cero. Antes no
              existía NINGUNA manera de abrir una conversación nueva sin borrar
              la actual. */}
          {messages.length > 0 && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => {
                  stream.abort();
                  setMessages([]);
                  setMode(null);
                  history.startNewConversation();
                }}
                className="inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full border border-[#b6dded] bg-white text-black hover:bg-slate-50 transition-all shadow-sm"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {t("ipmAssistant.newConversation", "Nueva conversación")}
              </button>
            </div>
          )}
          {messages.length === 0 ? (
            /* ── Empty State with history + quick actions ──
               Compacto a propósito: avatar y aires reducidos para que la tarjeta de
               última conversación entre COMPLETA sobre el composer en la ventana
               del micrófono, en móvil, tablet y escritorio. */
            <div className="flex flex-col items-center py-4 px-3 md:pb-6">
              {/* Ulture AI avatar + saludo por franja horaria (bienvenida estilo app de Claude) */}
              <img
                src={ultureLogo}
                alt="Ulture Pest"
                draggable={false}
                className="w-16 h-16 rounded-[18px] object-contain bg-white mb-3 shadow-sm border border-border/60 select-none p-2.5"
              />
              <p className="text-sm text-muted-foreground mb-0.5">
                {greeting}{firstName ? `, ${firstName}` : ""}
              </p>
              <p className="text-lg font-bold text-foreground mb-3">
                {t("ipmAssistant.howCanIHelp", "¿En qué puedo ayudarte?")}
              </p>

              {/* Sugerencias de ESTA pantalla. Una sola fila que se desliza de
                  lado: el vacío es compacto a propósito para que la última
                  conversación quepa entera sobre el composer en la hoja del
                  micrófono, y una segunda fila la empujaría. */}
              <div className="w-full mb-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="mx-auto flex w-max gap-1.5 pb-0.5">
                {suggestions.map((s) => {
                  const text = suggestionText(s, i18n.language, t);
                  /* La sugerencia del cuaderno ABRE el modo si la empresa lo tiene;
                     si no, es la pregunta de ayuda de siempre. */
                  const sm = suggestionMode(s.key);
                  const smDef = sm && availableModes.some((m) => m.id === sm) ? modeById(sm) : undefined;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      data-testid={`assistant-suggestion-${s.key}`}
                      onClick={() => {
                        if (smDef) {
                          setMode(smDef.id);
                          void sendMessage(t(smDef.openerKey), smDef.id);
                        } else {
                          void sendMessage(text);
                        }
                      }}
                      disabled={isLoading}
                      className="shrink-0 whitespace-nowrap rounded-full border border-[#b6dded] bg-white px-3.5 min-h-[36px] text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-[#f4f9ff] active:bg-[#eaf3fd] disabled:opacity-50"
                    >
                      {text}
                    </button>
                  );
                })}
                </div>
              </div>

              {/* Recent conversations — tarjeta blanca con filas divididas, icono en
                  chip y papelera siempre visible (el hover-only no existía en táctil) */}
              {history.conversations.length > 0 && (
                <div className="w-full">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 px-1">
                    {t("ipmAssistant.recentConversation")}
                  </p>
                  <div className="rounded-2xl border border-[#e2ecf5] bg-white shadow-sm divide-y divide-[#eef3f8] overflow-hidden">
                    {/* Filas compactas: la tarjeta asomaba cortada bajo el composer
                        en la ventana del micrófono; con menos alto entra en el
                        primer vistazo sin tener que bajar. */}
                    {history.conversations.slice(0, 3).map((conv) => (
                      <div
                        key={conv.id}
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors hover:bg-[#f4f9ff] active:bg-[#eaf3fd]"
                        onClick={() => history.loadConversation(conv.id)}
                      >
                        <div className="w-7 h-7 rounded-full bg-[#2E84DE] flex items-center justify-center shrink-0">
                          <MessageSquare className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-[13px] font-semibold text-foreground">{conv.title}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true, locale: dateFnsLocale })}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); history.deleteConversation(conv.id); }}
                          aria-label={t("common.delete", "Eliminar")}
                          className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── Messages ── */
            messages.filter((msg) => !msg.hidden).map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={cn("flex gap-2.5", msg.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500/15 to-teal-600/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-sm">🧑‍🌾</span>
                  </div>
                )}
                <div
                  className={cn(
                    "rounded-2xl text-sm shadow-sm",
                    msg.recognition
                      ? "max-w-[92%] bg-card border border-[#b6dded] p-0 overflow-hidden"
                      : "max-w-[82%] px-3.5 py-2.5",
                    !msg.recognition && (msg.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted/50 border border-border/50 text-foreground rounded-bl-md')
                  )}
                >
                  {msg.recognition ? (
                    <RecognitionCard rec={msg.recognition} onCreateTask={msg.onCreateTask} t={t} />
                  ) : msg.role === 'assistant' ? (
                    <div>
                      {/* Herramientas consultadas — girando mientras corren, check al acabar. */}
                      {msg.toolEvents && msg.toolEvents.length > 0 && (
                        <ToolActivityChips tools={msg.toolEvents} />
                      )}
                      {/* Esperando la primera trama: los puntos viven DENTRO de la burbuja. */}
                      {msg.status === 'pending' && !msg.content && (
                        <div className="flex items-center gap-1.5 py-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      )}
                      <div className="prose prose-sm max-w-none break-words [overflow-wrap:anywhere] prose-headings:font-bold prose-headings:text-foreground prose-headings:mb-2 prose-headings:mt-3 prose-p:my-1.5 prose-p:leading-relaxed prose-li:my-0.5 prose-strong:text-foreground prose-ul:my-1.5 prose-ol:my-1.5 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_h2]:text-sm [&_h2]:border-b [&_h2]:border-border/30 [&_h2]:pb-1 [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:mt-2 [&_h3]:mb-1">
                        <AssistantMarkdown content={msg.content} />
                      </div>
                      {/* Tarjetas: van DEBAJO del texto, que hace de resumen. Se
                          pintan con las filas de la consulta, no con la prosa. */}
                      {msg.docs?.map((d, di) => <AssistantReportCard key={di} doc={d} />)}
                      {msg.cards?.map((g, gi) =>
                        g.kind === "task"
                          ? <AssistantTaskCards key={gi} items={g.items as AssistantTaskCardItem[]} />
                          : g.kind === "cuaderno"
                          ? g.items.map((it, ii) => (
                              <AssistantCuadernoCard
                                key={`${gi}-${ii}`}
                                year={Number(it.year)}
                                titularLocationId={typeof it.titularLocationId === "string" ? it.titularLocationId : null}
                                draftId={typeof it.draftId === "string" ? it.draftId : null}
                                seleccion={leerSeleccion(it.seleccion)}
                              />
                            ))
                          : g.kind === "cuaderno_change"
                          ? g.items.map((it, ii) => (
                              /* CU9: un cambio del documento que llega en este turno se aplica solo (sin seguimiento). */
                              <AssistantCuadernoChangeCard key={`${gi}-${ii}`} item={it} onAplicado={seguirTrasAplicar} autoAplicar={!!msg.liveCards} />
                            ))
                          : g.kind === "document_fill"
                          ? g.items.map((it, ii) => <AssistantDocumentFillCard key={`${gi}-${ii}`} item={it} />)
                          : g.kind === "marca_cambio"
                          ? g.items.map((it, ii) => (
                              /* F4: un dato de la cabecera; al guardarlo, el chat rehace el informe. */
                              <AssistantMarcaCambioCard key={`${gi}-${ii}`} item={it} onGuardado={seguirTrasGuardarCabecera} />
                            ))
                          : g.kind === "estructura_cambio"
                          ? g.items.map((it, ii) => (
                              /* «Campos y numeración» (23-10-2026): campos, valores y numeraciones; guarda el admin. */
                              <AssistantEstructuraCambioCard key={`${gi}-${ii}`} item={it} />
                            ))
                          : g.kind === "email"
                          ? g.items.map((it, ii) => (
                              /* «Envíalo por email» (A21): el informe es el de la conversación HASTA este mensaje. */
                              <AssistantEmailCard
                                key={`${gi}-${ii}`}
                                item={it}
                                docs={messages.slice(0, messages.indexOf(msg) + 1).flatMap((m) => m.docs ?? [])}
                              />
                            ))
                          : null
                      )}
                      {msg.modeNotServed && (
                        <p data-testid="assistant-mode-not-served" className="mt-1.5 text-[10px] italic text-muted-foreground">
                          {t("ipmAssistant.mode.notServed", "El cuaderno de campo no está disponible ahora: te he contestado en el modo normal.")}
                        </p>
                      )}
                      {msg.interrupted && (
                        <p className="mt-1.5 text-[10px] italic text-muted-foreground">
                          {t("ipmAssistant.streamInterrupted", "Se interrumpió la conexión; respuesta parcial.")}
                        </p>
                      )}
                      {/* Botones «llévame ahí»: el destino exacto (open_in_app y la
                          guía) o, si no, la página del tema. Máximo dos, cierran
                          el panel y navegan por dentro. */}
                      {msg.status !== 'pending' && msg.status !== 'streaming' && (
                        <AssistantActionButtons items={collectActionButtons(msg.cards, msg.nav)} />
                      )}
                      {msg.sources && msg.sources.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {msg.sources.map((s, si) => (
                            <a
                              key={si}
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-background/70 px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                            >
                              <ExternalLink className="w-3 h-3 shrink-0" />
                              <span className="max-w-[220px] truncate">{s.title}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {msg.documentos?.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {msg.documentos.map((d) => <AssistantDocumentChip key={d.id} doc={d} />)}
                        </div>
                      ) : null}
                      {msg.imagePreview && (
                        <img
                          src={msg.imagePreview}
                          alt="user upload"
                          className="rounded-xl max-h-60 w-auto object-cover"
                        />
                      )}
                      {msg.content && (
                        <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                      )}
                    </div>
                  )}
                  {msg.timestamp && (
                    <span className={cn(
                      "text-[10px] mt-1 block",
                      msg.role === 'user' ? 'text-primary-foreground/50' : 'text-muted-foreground/50'
                    )}>
                      {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-lg bg-primary/80 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="w-3.5 h-3.5 text-primary-foreground" />
                  </div>
                )}
              </motion.div>
            ))
          )}

          {/* El indicador de «escribiendo» vive ahora DENTRO de la burbuja
              placeholder (status pending) — junto con los chips de herramientas,
              que cuentan la verdad de la espera en vez de unos puntos mudos. */}
        </div>
      </div>

      {/* ── Recording Indicator ── */}
      <AnimatePresence>
        {audioState === "recording" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-1"
          >
            <div className="flex items-center gap-2 text-xs text-primary animate-pulse">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              {t("ipmAssistant.recording")}
            </div>
          </motion.div>
        )}
        {audioState === "transcribing" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-1"
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {t("ipmAssistant.transcribing")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Composer ──
          Mobile/tablet: fixed floating pill above the home indicator (ChatGPT-style).
          Desktop: inline inside the card, top-bordered like before. */}
      <div
        className={cn(
          embedded
            ? "relative border-t border-border/30 bg-white px-3 pt-2 pb-[max(0.75rem,var(--safe-bottom))] md:p-3"
            : cn(
                "lg:relative lg:border-t lg:border-border/30 lg:bg-muted/10 lg:p-3",
                "fixed left-0 right-0 bottom-0 z-40 px-3 pt-2 bg-gradient-to-t from-background via-background/95 to-transparent lg:bg-none lg:from-transparent lg:via-transparent lg:to-transparent",
              ),
        )}
        style={embedded ? undefined : { paddingBottom: 'calc(var(--safe-bottom) + 0.75rem)' }}
      >
        <div className="mx-auto max-w-3xl">
          {/* La «@» abre el menú de modos encima de la píldora (CQ4b). */}
          <AssistantModeMenu
            open={!!modeMenu}
            modes={menuModes}
            active={modeActive}
            onActiveChange={setModeActive}
            onSelect={selectMode}
            onClose={() => setModeMenu(null)}
          >
          <div className="rounded-[26px] border border-[#b6dded] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.08)] px-2 py-1.5 lg:rounded-2xl lg:shadow-sm">
            {/* El modo puesto: chip con su × para volver al asistente normal. */}
            {modeDef && (
              <div className="px-1 pt-1 pb-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <AssistantModeChip mode={modeDef} onClear={() => setMode(null)} />
                  {modeDef.id === "cuaderno" && (
                    <AssistantCuadernoTitular companyId={company?.id} value={cuadernoTitular} onChange={setCuadernoTitular} />
                  )}
                </div>
              </div>
            )}
            {/* Attached photo thumbnail row (inside the pill, above the input) */}
            {attachedPhotoPreview && (
              <div className="px-1 pt-1.5 pb-1">
                <div className="relative inline-block">
                  <img
                    src={attachedPhotoPreview}
                    alt="attachment"
                    className="h-16 w-16 rounded-xl object-cover border border-border/40"
                  />
                  <button
                    type="button"
                    onClick={clearAttachedPhoto}
                    aria-label={t("common.remove", "Remove")}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-foreground/80 text-background flex items-center justify-center shadow-sm hover:bg-foreground"
                  >
                    <span className="text-[11px] leading-none">×</span>
                  </button>
                  {isAnalyzingPhoto && (
                    <div className="absolute inset-0 rounded-xl bg-background/60 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-foreground" />
                    </div>
                  )}
                </div>
              </div>
            )}
            {(adjuntos.length > 0 || subiendoDocs.length > 0) && (
              <div className="flex flex-wrap gap-1.5 px-1 pt-1.5 pb-1" data-testid="assistant-document-attachments">
                {subiendoDocs.map((d) => <AssistantDocumentChip key={d.clave} doc={d} subiendo />)}
                {adjuntos.map((d) => (
                  <AssistantDocumentChip key={d.id} doc={d} onQuitar={() => setAdjuntos((p) => p.filter((x) => x.id !== d.id))} />
                ))}
              </div>
            )}
            <div className="flex items-end gap-1.5">
              {onAnalyzePhoto && (
                <>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </>
              )}
              {/* El «+»: foto (si la pantalla la analiza) y los modos. En el móvil
                  es la vía principal al cuaderno: la «@» está en el segundo teclado. */}
              {puedeSubirDocumentos && (
                <input
                  ref={docInputRef}
                  type="file"
                  accept={ACEPTA}
                  multiple
                  className="hidden"
                  data-testid="assistant-document-input"
                  onChange={(e) => {
                    void subirDocs(e.target.files);
                    e.target.value = "";
                  }}
                />
              )}
              {(onAnalyzePhoto || availableModes.length > 0 || puedeSubirDocumentos) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        disabled={isLoading || isAnalyzingPhoto || audioState !== "idle"}
                        size="icon"
                        variant="ghost"
                        className="h-10 w-10 rounded-full shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted/60"
                        title={onAnalyzePhoto ? t("ipmAssistant.analyzePhoto") : t("ipmAssistant.mode.menuTitle", "Modos")}
                        data-testid="assistant-plus"
                      >
                        <Plus className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top" className="w-56">
                      {onAnalyzePhoto && (
                        <>
                          <DropdownMenuItem onClick={() => cameraInputRef.current?.click()}>
                            <Camera className="w-4 h-4 mr-2" />
                            {t("common.capture.takePhoto", "Take photo")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                            <ImageIcon className="w-4 h-4 mr-2" />
                            {t("common.capture.chooseFromGallery", "Choose from gallery")}
                          </DropdownMenuItem>
                        </>
                      )}
                      {puedeSubirDocumentos && (
                        <DropdownMenuItem onClick={() => docInputRef.current?.click()} data-testid="assistant-plus-document">
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          {t("ipmAssistant.documents.attach", "Documento (Excel, CSV, Word)")}
                        </DropdownMenuItem>
                      )}
                      {(onAnalyzePhoto || puedeSubirDocumentos) && availableModes.length > 0 && <DropdownMenuSeparator />}
                      {availableModes.map((m) => (
                        <DropdownMenuItem key={m.id} onClick={() => selectMode(m.id)} data-testid={`assistant-plus-mode-${m.id}`}>
                          <BookOpenCheck className="w-4 h-4 mr-2" />
                          {t(m.labelKey)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
              )}
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                enterKeyHint="send"
                data-testid="assistant-input"
                placeholder={modeDef ? t(modeDef.placeholderKey) : t("ipmAssistant.placeholder")}
                className="flex-1 min-h-[40px] max-h-[40vh] resize-none overflow-y-auto bg-transparent border-0 px-2 py-2 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-0"
                disabled={isLoading || isAnalyzingPhoto || audioState !== "idle"}
                rows={1}
              />
              {/* Un solo botón a la derecha, como en la app de Claude: micrófono cuando
                  no hay nada escrito; al teclear pasa a Enviar; MIENTRAS el asistente
                  responde es Detener (corta el stream y se queda lo recibido). */}
              {stream.isStreaming ? (
                <Button
                  onClick={() => stream.abort()}
                  size="icon"
                  className={cn(
                    "h-10 w-10 rounded-full shrink-0 border-0 text-white shadow-sm transition-all",
                    "bg-gradient-to-br from-sky-400 to-blue-600 hover:from-sky-500 hover:to-blue-700",
                  )}
                  title={t("ipmAssistant.stop", "Detener")}
                >
                  <Square className="w-4 h-4 fill-current" />
                </Button>
              ) : (input.trim().length > 0 || attachedPhoto || adjuntos.length > 0) && audioState === "idle" ? (
                <Button
                  onClick={handleSend}
                  data-testid="assistant-send"
                  disabled={isLoading || isAnalyzingPhoto || subiendoDocs.length > 0}
                  size="icon"
                  className={cn(
                    "h-10 w-10 rounded-full shrink-0 border-0 text-white shadow-sm transition-all",
                    "bg-gradient-to-br from-sky-400 to-blue-600 hover:from-sky-500 hover:to-blue-700",
                    (isLoading || isAnalyzingPhoto) && "opacity-40",
                  )}
                  title={t("common.send", "Enviar")}
                >
                  {(isLoading || isAnalyzingPhoto) ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              ) : (
                <Button
                  data-testid="assistant-mic"
                  onClick={handleMicClick}
                  disabled={isLoading || isAnalyzingPhoto || audioState === "transcribing"}
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "h-10 w-10 rounded-full shrink-0 transition-colors",
                    audioState === "recording"
                      ? "bg-red-500 text-white hover:bg-red-600"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                  )}
                  title={t("ipmAssistant.recording")}
                >
                  {audioState === "recording" ? (
                    <Square className="w-5 h-5" />
                  ) : audioState === "transcribing" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </Button>
              )}
            </div>
          </div>
          </AssistantModeMenu>
        </div>

      </div>
    </div>
  );
});

export default IpmAiAssistant;

function getSeverityColor(severity: string) {
  const s = (severity || '').toLowerCase();
  if (s === 'low' || s === 'bajo') return 'bg-green-500 text-white';
  if (s === 'medium' || s === 'medio') return 'bg-yellow-500 text-white';
  if (s === 'high' || s === 'alto') return 'bg-orange-500 text-white';
  if (s === 'critical' || s === 'critico') return 'bg-red-500 text-white';
  return 'bg-gray-500 text-white';
}

function RecognitionCard({ rec, onCreateTask, t }: { rec: RecognitionPayload; onCreateTask?: () => void; t: (k: string, o?: any) => string }) {
  /* Con la lista del cultivo (F7) la IA también dice «sana», «no se puede saber», «es un
     auxiliar» o «es abiótico»: lo mismo que la ficha del parte (`lib/pests/desenlaceIA`). */
  const lectura = leerDesenlace({
    identified: rec.identified && !!rec.data,
    pestName: rec.data?.pestName,
    outcome: rec.data?.outcome ?? rec.outcome ?? null,
    outsideList: rec.data?.outsideList ?? null,
    listSource: rec.data?.listSource ?? null,
  });
  if (!rec.identified || !rec.data) {
    return (
      <div className="p-4 text-center" data-testid="recognition-card" data-outcome={lectura?.desenlace ?? "nada"}>
        <Camera className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
        {lectura && (lectura.desenlace === "sana" || lectura.desenlace === "no_se_sabe") ? (
          <AiNoPestMessage
            lectura={lectura}
            fallback={{ title: t('advanced.pestNotIdentified'), body: t('advanced.pestNotIdentifiedHint') }}
            className="[&>p:first-child]:justify-center"
          />
        ) : (
          <>
            <p className="text-sm font-semibold">{t('advanced.pestNotIdentified')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('advanced.pestNotIdentifiedHint')}</p>
          </>
        )}
      </div>
    );
  }
  const d = rec.data;
  /* La MISMA regla que el parte de campo (`lib/pestDiagnosis`): por debajo de
     60 % es una sospecha y se dice así, sin gravedad inventada. */
  const sospecha = estadoDiagnostico({ identified: rec.identified, pestName: d.pestName, confidence: d.confidence }) === "sospecha";
  const nombre = lectura?.nombre || limpiarNombrePlaga(d.pestName);
  // Un auxiliar no tiene gravedad de plaga (el servidor le pone «none»): no se pinta.
  const conGravedad = !sospecha && lectura?.desenlace !== "auxiliar" && !!d.severity && d.severity !== "none";
  return (
    <div className="p-4 space-y-3" data-testid="recognition-card" data-outcome={lectura?.desenlace ?? "plaga"}>
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('ipmAssistant.photoResultIntro')}</p>
      <div>
        <h4 className="text-base font-bold leading-tight">
          {sospecha ? t('pests.scouting.aiSuspect', { name: nombre }) : nombre}
        </h4>
        {/* Sin la coletilla, el nombre de un auxiliar ES el científico («Orius laevigatus»): no se repite. */}
        {d.scientificName && d.scientificName.trim().toLowerCase() !== nombre.trim().toLowerCase() && (
          <p className="text-xs italic text-muted-foreground">{d.scientificName}</p>
        )}
      </div>
      <AiOutcomeChips lectura={lectura} cropCode={rec.cropCode ?? null} />
      <div className="flex flex-wrap gap-1.5">
        {conGravedad && (
          <Badge className={cn('text-[10px] px-2 py-0.5', getSeverityColor(d.severity))}>
            {t(`advanced.severityLevels.${(d.severity || '').toLowerCase()}`) || d.severity}
          </Badge>
        )}
        {/* La categoría llega en inglés («insect»): traducida si es de las conocidas. */}
        {d.category && (
          <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
            {t(`pests.pestTypes.${String(d.category).toLowerCase()}`, { defaultValue: d.category })}
          </Badge>
        )}
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>{t('advanced.confidence')}</span>
          <span className="font-bold">{(d.confidence * 100).toFixed(0)}%</span>
        </div>
        <Progress value={d.confidence * 100} className="h-1.5" />
      </div>
      {sospecha && (
        <div className="space-y-0.5 text-xs text-muted-foreground">
          {!!rec.candidates?.length && (
            <p>{t('pests.scouting.aiCandidates', { list: rec.candidates.join(', ') })}</p>
          )}
          <p>{t('pests.scouting.aiNeedsBetterPhoto')}</p>
        </div>
      )}
      <div className="rounded-lg bg-muted/40 p-3 space-y-1">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
          <Bug className="w-3 h-3" /> {t('advanced.pestDetails')}
        </div>
        <p className="text-xs leading-relaxed text-foreground/80">{d.description}</p>
      </div>
      <div className="rounded-lg bg-primary/5 p-3 space-y-1 border border-primary/20">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
          <FileText className="w-3 h-3" /> {t('advanced.recommendedTreatment')}
        </div>
        <p className="text-xs leading-relaxed text-foreground/80">{d.recommendedTreatment}</p>
      </div>
      {/* Un tratamiento es para una PLAGA: con un auxiliar o algo abiótico no se ofrece. */}
      {onCreateTask && (lectura?.esPlaga ?? true) && (
        <Button onClick={onCreateTask} className="w-full bg-[#2E84DE] hover:bg-[#2E84DE]/90 text-white text-xs h-9">
          {t('advanced.createTaskFromScan')}
        </Button>
      )}
      {lectura && !lectura.esPlaga && (
        <p className="text-[11px] leading-snug text-muted-foreground" data-testid="recognition-no-treatment">
          {lectura.desenlace === "auxiliar"
            ? t('pests.aiOutcome.noTreatmentBeneficial', 'Es fauna útil: ayuda a controlar las plagas. No se crea un tratamiento.')
            : t('pests.aiOutcome.noTreatmentAbiotic', 'No es una plaga: no se crea un tratamiento contra plagas.')}
        </p>
      )}
    </div>
  );
}

